import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './Notifications.css';
import './GroupDetails.css';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { API_BASE, authHeaders } from '../constants/api';
import { severityLabels } from '../constants/alarmConstants';
import { useDropdownClose } from '../hooks/useDropdownClose';
import { updateAlarmStatusAPI } from '../utils/alarmActions';
import { formatDateDE } from '../utils/formatDate';
import Pagination from './Pagination';
import { useAuth } from '../context/AuthContext';
import { Öffnen, Löschen, Abbrechen } from '../icons';
import { AlarmStatusDropdown } from '../UI-Elements';

const PAGE_SIZE = 4;
const FETCH_LIMIT = 1000;

// Module-level dedupe helpers to avoid immediate duplicate requests
let _lastNotificationsFetchKey = null;
let _lastNotificationsFetchTime = 0;
const _NOTIF_DEDUPE_MS = 500;

function Notifications() {
  
  const [alarms, setAlarms] = useState([]);
  const [allAlarms, setAllAlarms] = useState([]);
  const [alarmsCount, setAlarmsCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotificationDeleteConfirm, setShowNotificationDeleteConfirm] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [deletingNotification, setDeletingNotification] = useState(false);
  const notificationDeleteRef = useRef(null);

  const [alarmToDelete, setAlarmToDelete] = useState(null);
  const [showAlarmDeleteConfirm, setShowAlarmDeleteConfirm] = useState(false);
  const [deletingAlarm, setDeletingAlarm] = useState(false);
  const alarmDeleteRef = useRef(null);

  const [openAlarmStatusMenu, setOpenAlarmStatusMenu] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState('success');

  const { authToken: token } = useAuth();
  const navigate = useNavigate();

  const deviceNameCacheRef = useRef(new Map());
  const [originatorNames, setOriginatorNames] = useState({});

  const closeAlarmStatusMenu = useCallback(() => setOpenAlarmStatusMenu(null), []);
  useDropdownClose(openAlarmStatusMenu, closeAlarmStatusMenu, '.alarm-status-wrapper');

  function isAlarmNotification(n) {
    try {
      if (!n) return false;
      if (n.type && String(n.type).toLowerCase().includes('alarm')) return true;
      if (n.data && JSON.stringify(n.data).toLowerCase().includes('alarm')) return true;
      if (n.originator && n.originator.entityType && String(n.originator.entityType).toLowerCase().includes('alarm')) return true;
      return false;
    } catch (e) {
      return false;
    }
  }

  useFocusTrap(showNotificationDeleteConfirm, notificationDeleteRef);
  useFocusTrap(showAlarmDeleteConfirm, alarmDeleteRef);

  async function toggleRead(n) {
    const notifId = n.id?.id ?? n.id;
    const isRead = n.status === 'READ';
    // optimistic UI
    setNotifications(prev => prev.map(x => (x.id?.id ?? x.id) === notifId ? { ...x, status: isRead ? 'SENT' : 'READ' } : x));
    try {
      if (!isRead) {
        await fetch(`${API_BASE}/notification/${notifId}/read`, { method: 'PUT', headers: authHeaders(token) });
      }
      // ThingsBoard has no unread endpoint — optimistic only for toggle back
    } catch (e) {
      // revert on error
      setNotifications(prev => prev.map(x => (x.id?.id ?? x.id) === notifId ? { ...x, status: n.status } : x));
    }
  }

  function confirmDeleteNotification(n) {
    setNotificationToDelete(n);
    setShowNotificationDeleteConfirm(true);
  }

  async function performDeleteNotification() {
    if (!notificationToDelete) return;
    setDeletingNotification(true);
    const notifId = notificationToDelete.id?.id ?? notificationToDelete.id;
    try {
      const res = await fetch(`${API_BASE}/notification/${notifId}`, { method: 'DELETE', headers: authHeaders(token) });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Benachrichtigung konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setNotifications(prev => prev.filter(x => (x.id?.id ?? x.id) !== notifId));
      setStatusType('success');
      setStatusMessage('Benachrichtigung wurde gelöscht.');
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (e) {
      console.error('Failed to delete notification', e);
      setStatusType('error');
      setStatusMessage(`Benachrichtigung konnte nicht gelöscht werden: ${e.message || 'Netzwerkfehler'}.`);
      setTimeout(() => setStatusMessage(''), 4000);
    } finally {
      setDeletingNotification(false);
      setShowNotificationDeleteConfirm(false);
      setNotificationToDelete(null);
    }
  }

  async function deleteAlarm() {
    if (!alarmToDelete) return;
    try {
      setDeletingAlarm(true);
      const res = await fetch(`${API_BASE}/alarm/${alarmToDelete.id}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarm konnte nicht gelöscht werden (HTTP ${res.status})`);
      }
      setAlarms(prev => prev.filter(a => (a.id?.id ?? a.id) !== alarmToDelete.id));
      setAllAlarms(prev => prev.filter(a => (a.id?.id ?? a.id) !== alarmToDelete.id));
      setStatusType('success');
      setStatusMessage(`Alarm „${alarmToDelete.name}“ wurde erfolgreich gelöscht.`);
      setTimeout(() => setStatusMessage(''), 4000);
    } catch (err) {
      setStatusType('error');
      setStatusMessage(`Alarm konnte nicht gelöscht werden: ${err.message || 'Netzwerkfehler'}.`);
      setTimeout(() => setStatusMessage(''), 4000);
    } finally {
      setDeletingAlarm(false);
      setShowAlarmDeleteConfirm(false);
      setAlarmToDelete(null);
    }
  }

  const fetchDeviceNameIfNeeded = useCallback(async function fetchDeviceNameIfNeeded(originator) {
    if (!originator) return null;
    const id = originator?.id?.id ?? originator?.id ?? originator;
    if (!id) return null;
    const key = String(id);
    if (deviceNameCacheRef.current.has(key)) return deviceNameCacheRef.current.get(key);
    const candidates = [
      `${API_BASE}/device/${key}`,
      `${API_BASE}/devices/${key}`,
    ];
    for (const url of candidates) {
      try {
        const res = await fetch(url, { headers: authHeaders(token) });
        if (!res.ok) continue;
        const data = await res.json().catch(() => null);
        const name = data && (data.name || data.deviceName || data.label || data.title);
        if (name) {
          deviceNameCacheRef.current.set(key, name);
          setOriginatorNames(prev => ({ ...prev, [key]: name }));
          return name;
        }
      } catch { /* try next */ }
    }
    deviceNameCacheRef.current.set(key, key);
    setOriginatorNames(prev => ({ ...prev, [key]: key }));
    return key;
  }, [token]);

  // Prefetch originator names for visible alarms
  useEffect(() => {
    const idsToFetch = [];
    for (const a of alarms || []) {
      const id = a?.originator?.id?.id ?? a?.originator?.id ?? a?.originator;
      const key = id && String(id);
      if (key && !deviceNameCacheRef.current.has(key)) idsToFetch.push(key);
    }
    if (idsToFetch.length === 0) return;
    (async () => {
      for (const k of idsToFetch) {
        try { await fetchDeviceNameIfNeeded({ id: k }); } catch (_) {}
      }
    })();
  }, [alarms, fetchDeviceNameIfNeeded]);

  async function openAlarmRule(alarm) {
    try {
      const originatorId = alarm?.originator?.id?.id ?? alarm?.originator?.id ?? alarm?.originator;
      const params = new URLSearchParams({
        pageSize: '50',
        page: '0',
        types: 'ALARM',
        sortProperty: 'createdTime',
        sortOrder: 'DESC',
        textSearch: alarm.type || alarm.name || '',
      });
      const res = await fetch(`${API_BASE}/calculatedFields?${params.toString()}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => null);
        throw new Error(errData?.detail || errData?.message || `Alarmregel konnte nicht geladen werden (HTTP ${res.status})`);
      }
      const data = await res.json();
      const rules = data.data || [];
      const match = rules.find(r =>
        r.name === (alarm.type || alarm.name) &&
        (r.entityId?.id === originatorId || !originatorId)
      ) || rules.find(r => r.name === (alarm.type || alarm.name));
      if (match) {
        navigate(`/alarm-rules/${match.id?.id}`);
      } else {
        setStatusType('error');
        setStatusMessage('Zugehörige Alarmregel nicht gefunden.');
        setTimeout(() => setStatusMessage(''), 4000);
      }
    } catch (err) {
      setStatusType('error');
      setStatusMessage(`Alarmregel konnte nicht geladen werden: ${err.message || 'Netzwerkfehler'}.`);
      setTimeout(() => setStatusMessage(''), 4000);
    }
  }

    useEffect(() => {
      async function loadAll() {
        try {
          setLoading(true);
          setError("");
  
          const headers = authHeaders(token);

          // dedupe quick successive calls (covers StrictMode double-invoke)
          const key = `${token || ''}|${page}`;
          const now = Date.now();
          if (_lastNotificationsFetchKey === key && now - _lastNotificationsFetchTime < _NOTIF_DEDUPE_MS) {
            return;
          }
          _lastNotificationsFetchKey = key;
          _lastNotificationsFetchTime = now;

          const [
            alarmsRes,
            notificationsRes
          ] = await Promise.all([
            fetch(`${API_BASE}/alarms?searchStatus=ANY&pageSize=${FETCH_LIMIT}&page=0&sortProperty=createdTime&sortOrder=DESC&fetchOriginator=true`, { headers }),
            fetch(`${API_BASE}/notifications?pageSize=${PAGE_SIZE}&page=${page}&sortProperty=createdTime&sortOrder=DESC&unreadOnly=false`, { headers })
          ]);
  
          if (!alarmsRes.ok) {
            const text = await alarmsRes.text().catch(() => '');
            throw new Error(`Alarme konnten nicht geladen werden (HTTP ${alarmsRes.status}${text ? ': ' + text.substring(0, 100) : ''})`);
          }
  
          if (!notificationsRes.ok) {
            const text = await notificationsRes.text().catch(() => '');
            throw new Error(`Benachrichtigungen konnten nicht geladen werden (HTTP ${notificationsRes.status}${text ? ': ' + text.substring(0, 100) : ''})`);
          }
  
          const alarmsData = await alarmsRes.json().catch(() => ({ data: [] }));
          const notificationsData = await notificationsRes.json().catch(() => ({ data: [] }));
  
          setAllAlarms(alarmsData.data || []);
          setNotifications(notificationsData.data || []);
  
        } catch (err) {
          console.error('Error loading alarms and notifications:', err);
          setError(err.message || "Alarme und Benachrichtigungen konnten nicht geladen werden. Bitte prüfen Sie Ihre Netzwerkverbindung.");
        } finally {
          setLoading(false);
        }
      }
  
      if (token) loadAll();
    }, [token, page]);

  // apply 7-day filter and client-side pagination
  useEffect(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;

    function getTs(a) {
      if (a.startTs) return Number(a.startTs);
      if (a.createdTime) return Number(a.createdTime);
      return 0;
    }

    const filtered = (allAlarms || []).filter(a => getTs(a) >= cutoff);

    const count = filtered.length;
    const pages = count ? Math.ceil(count / PAGE_SIZE) : 0;

    if (page >= pages && pages > 0) {
      setPage(pages - 1);
      return;
    }

    setAlarmsCount(count);
    setTotalPages(pages);
    const start = page * PAGE_SIZE;
    setAlarms(filtered.slice(start, start + PAGE_SIZE));
  }, [allAlarms, page]);

  async function ackAlarm(alarm) {
    await updateAlarmStatus(alarm, "ack", "ACTIVE_ACK");
  }

  async function clearAlarm(alarm) {
    await updateAlarmStatus(alarm, "clear", "CLEARED_ACK");
  }

  async function updateAlarmStatus(alarm, action, newStatus) {
    await updateAlarmStatusAPI(alarm.id.id, action, token);

    setAlarms(prev =>
      prev.map(a =>
        a.id.id === alarm.id.id ? { ...a, status: newStatus } : a
      )
    );

    setOpenAlarmStatusMenu(null);

    const labels = { ACTIVE_ACK: 'Alarm bestätigt', CLEARED_ACK: 'Alarm behoben' };
    setStatusType('success');
    setStatusMessage(labels[newStatus] || 'Status geändert');
    setTimeout(() => setStatusMessage(''), 4000);
  }

    if (loading) return <p>Lade Meldungen...</p>;
    if (error) return <div role="alert">{error}</div>;


  /* ===============================
     Render
  =============================== */
  return (
    <div className="notifications-page">
      {/* Toolbar */}
      <div className="device-toolbar">
        <div className="device-toolbar-left">
          <h1 className="device-title">
            Meldungen
          </h1>
        </div>
      </div>

      {statusMessage && (
        <div className={`status-bar${statusType === 'error' ? ' status-bar--error' : ''}`} role="status" aria-live="polite">
          <p>{statusMessage}</p>
        </div>
      )}

      {/* Listen */}
      <div className="device-table-container" role="region" aria-label="Alarme">
        <h2>Alarme der letzten 7 Tage ({alarmsCount})</h2>
        {loading ? (
          <p>Alarme werden geladen…</p>
        ) : error ? (
          <div role="alert" className="error-message">{error}</div>
        ) : (
          <>
            <div className="device-list" role="list" aria-label="Alarme">
              <div className="device-row device-header" role="row">
                <div>Bezeichnung</div>
                <div>Ausgelöst</div>
                <div>Status</div>
                <div>Öffnen</div>
                <div>Löschen</div>
              </div>
              {(!alarms || alarms.length === 0) ? (
                <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Es wurden keine Alarme in den letzten 7 Tagen ausgelöst</div>
              ) : (
                alarms.map(a => (
                  <div key={a.id && a.id.id ? a.id.id : a.id} className="list-item device-item alarm-item clickable-row" role="listitem"
                    tabIndex={0}
                    aria-label={`Alarm ${a.name} öffnen`}
                    onClick={(e) => { if (e.target.closest('button, .sort-wrapper')) return; openAlarmRule(a); }}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('button, .sort-wrapper')) { e.preventDefault(); openAlarmRule(a); } }}
                  >
                    <div>
                      <span className="alarm-name">{a.name}</span>
                      <span className={`severity-badge ${a.severity ? a.severity.toLowerCase() : ''}`}>{severityLabels[a.severity]}</span>
                    </div>
                    <div>{formatDateDE(a.createdTime)} von {(() => {
                      const id = a?.originator?.id?.id ?? a?.originator?.id ?? a?.originator;
                      const key = id && String(id);
                      const name = (key && originatorNames[key]) ? originatorNames[key] : (a?.originator?.name || key || 'Unbekannt');
                      return key ? (
                        <span
                          className="originator-link"
                          role="link"
                          tabIndex={0}
                          title={`Gerät ${name} öffnen`}
                          aria-label={`Gerät ${name} öffnen`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/devices/${key}`, { state: { name } }); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); navigate(`/devices/${key}`, { state: { name } }); } }}
                        >{name}</span>
                      ) : name;
                    })()}</div>
                    <div className="actions">
                      <AlarmStatusDropdown
                        alarmId={a.id && a.id.id ? a.id.id : a.id}
                        status={a.status}
                        isOpen={openAlarmStatusMenu === (a.id && a.id.id ? a.id.id : a.id)}
                        onToggle={() => { const id = a.id && a.id.id ? a.id.id : a.id; setOpenAlarmStatusMenu(openAlarmStatusMenu === id ? null : id); }}
                        onAck={() => ackAlarm(a)}
                        onClear={() => clearAlarm(a)}
                        onClose={() => setOpenAlarmStatusMenu(null)}
                      />
                    </div>
                    <div>
                      <button className="icon-button" title="Details öffnen" aria-label="Details öffnen" onClick={() => openAlarmRule(a)}><span aria-hidden="true"><Öffnen /></span></button>
                    </div>
                    <div>
                      <button
                        className="icon-button delete"
                        title="Alarm löschen"
                        aria-label={`Alarm ${a.name} löschen`}
                        onClick={() => { setAlarmToDelete({ id: a.id?.id ?? a.id, name: a.name }); setShowAlarmDeleteConfirm(true); }}
                      ><span aria-hidden="true"><Löschen /></span></button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            {alarmsCount > 0 && (
              <Pagination page={page} totalPages={totalPages} totalItems={alarmsCount} pageSize={PAGE_SIZE} onPageChange={setPage} label="Alarme" />
            )}
            {/* Notifications (non-alarm) */}
            <div className="device-table-container" role="region" aria-label="Benachrichtigungen">
              <h2>Benachrichtigungen</h2>
              <div className="notification-list" role="list">
                <div className="device-row device-header" role="row">
                  <div>Bezeichnung</div>
                  <div>Eingegangen</div>
                  <div>Status</div>
                  <div>Löschen</div>
                </div>
                {(() => {
                  const filteredNotifications = (notifications || []).filter(n => !isAlarmNotification(n));
                  if (filteredNotifications.length === 0) {
                    return (
                      <div className="no-items" style={{ padding: '12px 20px', color: '#474747' }}>Keine Benachrichtigungen</div>
                    );
                  }
                  return filteredNotifications.map(n => (
                    <div key={n.id?.id ?? n.id} className="notification-item" role="listitem">
                      <div>
                        <span className="alarm-name">{n.subject || n.title || n.text || n.name || (n.info && (n.info.description || n.info.subject)) || (n.data && n.data.title) || '—'}</span>
                      </div>
                      <div className="notification-time">{formatDateDE(n.createdTime || n.ts || n.tsCreated || n.ts)}</div>
                      <div className="notification-status">
                        <button
                          className={`sort-button ${n.status === 'READ' ? 'read' : 'unread'}`}
                          onClick={n.status === 'READ' ? undefined : () => toggleRead(n)}
                          disabled={n.status === 'READ'}
                          aria-label={n.status === 'READ' ? 'Benachrichtigung bereits gelesen' : 'Als gelesen markieren'}
                          title={n.status === 'READ' ? 'Bereits gelesen' : 'Als gelesen markieren'}
                        >
                          {n.status === 'READ' ? 'Gelesen' : 'Ungelesen'}
                        </button>
                      </div>
                      <div>
                        <button className="icon-button delete" onClick={() => confirmDeleteNotification(n)} title="Löschen"><Löschen /></button>
                      </div>
                    </div>
                  ));
                })() }
              </div>
            </div>
            {/* delete confirm modal for notifications */}
            {showNotificationDeleteConfirm && notificationToDelete && (
              <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-notification-title">
                <div className="modal" ref={notificationDeleteRef}>
                  <h2 id="delete-notification-title">Benachrichtigung löschen?</h2>
                  <p>Soll die Benachrichtigung wirklich gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden.</p>
                  <div className="modal-actions">
                    <button className="modal-primary" onClick={performDeleteNotification} disabled={deletingNotification}>🗑 Löschen bestätigen</button>
                    <button className="modal-secondary" onClick={() => { setShowNotificationDeleteConfirm(false); setNotificationToDelete(null); }} disabled={deletingNotification}>✕ Abbrechen</button>
                  </div>
                </div>
              </div>
            )}
            {/* delete confirm modal for alarms */}
            {showAlarmDeleteConfirm && alarmToDelete && (
              <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="delete-alarm-notif-title">
                <div className="modal" ref={alarmDeleteRef}>
                  <h2 id="delete-alarm-notif-title">Alarm löschen?</h2>
                  <p>
                    Soll der Alarm <i>{alarmToDelete.name}</i> wirklich gelöscht werden? Diese Aktion ist <strong>unwiderruflich</strong>. Das Löschen dieses ausgelösten Alarms hat keine Auswirkung auf die zugrunde liegende Alarmregel oder zukünftige Alarme. Es entfernt lediglich diesen spezifischen Alarm.
                  </p>
                  <div className="modal-actions">
                    <button className="modal-primary" onClick={deleteAlarm} disabled={deletingAlarm}>
                      {deletingAlarm ? (
                        "Löschen…"
                      ) : (
                        <>
                          <Löschen />
                          Löschen bestätigen
                        </>
                      )}
                    </button>
                    <button className="modal-secondary" onClick={() => { setShowAlarmDeleteConfirm(false); setAlarmToDelete(null); }} disabled={deletingAlarm}>
                      <span ariaHidden="true"><Abbrechen /></span> Abbrechen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Notifications;